import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Clock, AlertTriangle, CreditCard, X } from 'lucide-react';

const TrialBanner = () => {
  const { user, api } = useAuth();
  const [trialInfo, setTrialInfo] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Only show for logged-in admins with trial status
    if (!user || user.role !== 'admin' || !user.company_id) return;
    const status = user.company_status;
    const trialEnds = user.trial_ends_at;
    if (!status || !['trial_active', 'trial_expired'].includes(status)) return;

    if (trialEnds) {
      const endsAt = new Date(trialEnds);
      const now = new Date();
      const daysLeft = Math.max(0, Math.ceil((endsAt - now) / (1000 * 60 * 60 * 24)));
      setTrialInfo({ status, daysLeft, endsAt: endsAt.toLocaleDateString('fr-FR') });
    } else {
      setTrialInfo({ status, daysLeft: 0, endsAt: '' });
    }
  }, [user]);

  if (!trialInfo || dismissed) return null;

  if (trialInfo.status === 'trial_expired') {
    return (
      <div className="bg-red-500/10 border-b border-red-500/20 px-4 py-3" data-testid="trial-expired-banner">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
            <span className="text-sm text-red-300">
              <strong>Essai terminé</strong> — Votre période d'essai gratuit est expirée. Activez votre abonnement pour retrouver l'accès complet.
            </span>
          </div>
          <button
            onClick={() => window.location.href = '/dashboard'}
            data-testid="trial-activate-btn"
            className="px-4 py-1.5 rounded-lg text-xs font-bold text-white bg-red-500 hover:bg-red-600 flex items-center gap-1.5 transition-all flex-shrink-0"
          >
            <CreditCard className="w-3.5 h-3.5" /> Activer l'abonnement
          </button>
        </div>
      </div>
    );
  }

  // trial_active
  const isUrgent = trialInfo.daysLeft <= 1;
  return (
    <div className={`${isUrgent ? 'bg-orange-500/10 border-orange-500/20' : 'bg-blue-500/10 border-blue-500/20'} border-b px-4 py-2.5`} data-testid="trial-active-banner">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Clock className={`w-4 h-4 flex-shrink-0 ${isUrgent ? 'text-orange-400' : 'text-blue-400'}`} />
          <span className={`text-xs ${isUrgent ? 'text-orange-300' : 'text-blue-300'}`}>
            <strong>Essai gratuit</strong> — {trialInfo.daysLeft > 0 ? `${trialInfo.daysLeft} jour${trialInfo.daysLeft > 1 ? 's' : ''} restant${trialInfo.daysLeft > 1 ? 's' : ''}` : "Expire aujourd'hui"} (fin le {trialInfo.endsAt})
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setDismissed(true)}
            className="text-gray-500 hover:text-gray-300 p-1"
            data-testid="trial-dismiss-btn"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrialBanner;
